import { Route, Routes, useNavigate } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  useAuth,
  RedirectToSignIn,
} from '@clerk/clerk-react';

import HomePage from './pages/home';
import LeaderboardPage from './pages/leaderboard';
import ErrorPage from './pages/error';
import AboutPage from './pages/about';
import VotePage from './pages/vote';
import VoteCategoryPage from './pages/vote/category';
import LogoutPage from './pages/logout';
import TermsOfService from './pages/terms';
import PrivacyPolicy from './pages/privacy';
import { ScrollToTop } from './components/ScrollToTop';
import { NavBar } from './components/NavBar';

import './global.css';
import { useQuery } from '@tanstack/react-query';
import { getVotes } from './common/api/votes';
import { VotesResponse } from './common/types';
import { useAuthToken } from './common/useAuthToken';
import UserView from './pages/user';
import ProfileView from './pages/profile';
import UserUpdateView from './pages/user/update';
import { Footer } from './components/Footer';
import { TextLink } from './components/TextLink';

type ProtectedRouteProps = {
  children: React.ReactNode;
};

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return <>{children}</>;
}

function AppElements() {
  const { isSignedIn, isLoaded, userId } = useAuth();

  // Sync Clerk auth token with axios
  useAuthToken();

  const { data: votes } = useQuery({
    queryKey: ['votes', userId],
    queryFn: async () => await getVotes(),
    staleTime: 0,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
    enabled: isLoaded && isSignedIn,
  });
  const [liveVotes, setLiveVotes] = useState<VotesResponse[] | undefined>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLiveVotes(votes);
  }, [votes]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden"
    >
      <ScrollToTop scrollContainerRef={scrollContainerRef} />
      <NavBar votes={liveVotes} />
      <div className="flex-1 flex flex-col justify-between">
        <main className="pt-8">
          <Routes>
            {/* Auth routes */}
            <Route
              path="/sign-in/*"
              element={
                <div className="flex justify-center items-center min-h-[60vh]">
                  <SignIn
                    routing="path"
                    path="/sign-in"
                    signUpUrl="/sign-up"
                    fallbackRedirectUrl="/"
                  />
                </div>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <div className="flex flex-col justify-center items-center min-h-[60vh]">
                  <SignUp
                    routing="path"
                    path="/sign-up"
                    signInUrl="/sign-in"
                    fallbackRedirectUrl="/"
                  />
                  <div className="mt-4 text-center text-sm text-dark max-w-sm">
                    By signing up, you agree to our{' '}
                    <TextLink to="/terms" className="underline hover:text-primary">
                      Terms of Service
                    </TextLink>{' '}
                    and{' '}
                    <TextLink to="/privacy" className="underline hover:text-primary">
                      Privacy Policy
                    </TextLink>
                    .
                  </div>
                </div>
              }
            />

            {/* Protected routes */}
            <Route
              path="/vote"
              element={
                <ProtectedRoute>
                  <VotePage setLiveVotes={setLiveVotes} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vote/category/:name"
              element={
                <ProtectedRoute>
                  <VoteCategoryPage setLiveVotes={setLiveVotes} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user"
              element={
                <ProtectedRoute>
                  <UserView votes={liveVotes} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/update"
              element={
                <ProtectedRoute>
                  <UserUpdateView />
                </ProtectedRoute>
              }
            />

            {/* Public routes */}
            <Route path="/users/:username" element={<ProfileView />} />
            <Route path="/" element={<HomePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route element={<ErrorPage />} path="*" />
          </Routes>
        </main>
        <Footer />
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
      <AppElements />
    </div>
  );
}

export default App;
